// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Moving around the estimate grid the way a spreadsheet does.
 *
 * Estimators live in Excel, and every moment their hands leave the keyboard
 * is a moment they notice the software instead of the estimate. The grid used
 * to answer only to Tab, and only through two hand-written lookups that walked
 * the DOM -- so arrows moved the text caret, there was no way back up a
 * column, and the two lookups disagreed about what came after the last cell
 * in a row.
 *
 * One model now, and Tab and the arrow keys both read it, so they cannot
 * drift apart. It is pure: the caller says which rows and columns exist right
 * now and which cells are really there, and gets back where to go.
 */

/** The columns you can type in, left to right as they are drawn. */
export const NAV_FIELDS: readonly string[] = ["description", "qty", "unit", "unitCost"];

export interface Cell {
  itemId: string;
  field: string;
}

export type Dir = "up" | "down" | "left" | "right";

/** Whether a cell is on screen and can take focus. */
export type CellExists = (cell: Cell) => boolean;

const yes: CellExists = () => true;

/**
 * Where a key takes you, or null at the edge.
 *
 * `wrap` is the difference between Tab and an arrow. Tab runs off the end of
 * a row into the start of the next, which is how a spreadsheet fills a table.
 * An arrow stops, because in Excel there is always another column out there
 * to the right and pressing right does not fling you down a row.
 */
export function nextCell(
  rows: readonly string[],
  fields: readonly string[],
  from: Cell,
  dir: Dir,
  opts: { wrap?: boolean; exists?: CellExists } = {},
): Cell | null {
  const exists = opts.exists ?? yes;
  const r = rows.indexOf(from.itemId);
  const f = fields.indexOf(from.field);
  if (r === -1 || f === -1) return null;

  if (dir === "up" || dir === "down") {
    const step = dir === "down" ? 1 : -1;
    // Skip rows that do not carry this column -- a roll-up header has no
    // quantity of its own, and stopping on it would strand the keyboard.
    for (let i = r + step; i >= 0 && i < rows.length; i += step) {
      const c = { itemId: rows[i], field: from.field };
      if (exists(c)) return c;
    }
    return null;
  }

  const step = dir === "right" ? 1 : -1;
  let i = r, j = f + step;
  for (;;) {
    while (j >= 0 && j < fields.length) {
      const c = { itemId: rows[i], field: fields[j] };
      if (exists(c)) return c;
      j += step;
    }
    if (!opts.wrap) return null;
    i += step;
    if (i < 0 || i >= rows.length) return null;
    j = step > 0 ? 0 : fields.length - 1;
  }
}

/**
 * Ctrl+Arrow: the far end of the block you are in, or the next block along.
 *
 * Excel jumps to the edge of contiguous data; the grid's blocks are its
 * subcategories, which is the same idea with the boundaries already drawn.
 * Pressing it from the edge carries you into the next block rather than
 * doing nothing, so holding it walks the division.
 */
export function blockEdge(
  blocks: readonly (readonly string[])[],
  from: Cell,
  dir: "up" | "down",
): Cell | null {
  const bi = blocks.findIndex(b => b.includes(from.itemId));
  if (bi === -1) return null;
  const block = blocks[bi];
  const edge = dir === "down" ? block[block.length - 1] : block[0];
  if (edge !== from.itemId) return { itemId: edge, field: from.field };
  const step = dir === "down" ? 1 : -1;
  for (let i = bi + step; i >= 0 && i < blocks.length; i += step) {
    const nb = blocks[i];
    if (nb.length) return { itemId: dir === "down" ? nb[0] : nb[nb.length - 1], field: from.field };
  }
  return null;
}

/**
 * Whether a left/right arrow should leave the cell or move within the text.
 *
 * A cell you arrived at by keyboard has its whole value selected, so the
 * first arrow leaves again -- you are moving through the grid, not reading a
 * number. Once the caret is placed, arrows walk the text until they reach an
 * end, and then they leave. That is what makes a long description editable
 * without taking your hands off the arrow keys.
 */
export function leavesCell(
  dir: "left" | "right",
  caret: { start: number; end: number; length: number },
): boolean {
  if (caret.start !== caret.end) return true;
  return dir === "left" ? caret.start === 0 : caret.start >= caret.length;
}

/**
 * Ctrl+D, both of the things it means in a spreadsheet.
 *
 * Over a selected range it pushes the top value down the rest of it. On a
 * single cell it copies the one above, which is how you repeat a unit cost
 * down a list without selecting anything first.
 */
export function planFillDown(
  rows: readonly string[],
  range: readonly string[],
): { from: string; to: string[] } | null {
  if (range.length >= 2) return { from: range[0], to: range.slice(1) };
  const only = range[0];
  if (!only) return null;
  const i = rows.indexOf(only);
  if (i <= 0) return null;     // the top row has nothing above it to copy
  return { from: rows[i - 1], to: [only] };
}
